import type { ReactNode } from "react";
import { m } from "framer-motion";
import { cn } from "@/lib/utils";
import { fadeUp } from "@/lib/motion";

type SectionHeaderProps = {
  eyebrow?: string;
  eyebrowIcon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  align?: "center" | "left";
  tone?: "dark" | "light";
  divider?: boolean;
  className?: string;
  titleAs?: "h1" | "h2" | "h3";
};

/**
 * Cabeçalho padronizado de section: eyebrow chip + título + descrição + divider gold.
 * Usado em todas as sections do site para hierarquia consistente.
 */
export function SectionHeader({
  eyebrow,
  eyebrowIcon,
  title,
  description,
  align = "center",
  tone = "dark",
  divider = false,
  className,
  titleAs = "h2",
}: SectionHeaderProps) {
  const TitleTag = titleAs;
  const isLight = tone === "light";
  const alignClass =
    align === "center" ? "text-center items-center" : "text-left items-start";
  const containerClass = align === "center" ? "max-w-2xl mx-auto" : "max-w-2xl";

  return (
    <m.div
      variants={fadeUp}
      className={cn(
        "flex flex-col gap-3",
        alignClass,
        containerClass,
        className
      )}
    >
      {eyebrow && (
        <span className={cn("sr-eyebrow", isLight && "sr-eyebrow--light")}>
          {eyebrowIcon && (
            <span className="inline-flex items-center [&_svg]:w-3.5 [&_svg]:h-3.5">
              {eyebrowIcon}
            </span>
          )}
          {eyebrow}
        </span>
      )}

      <TitleTag
        className={cn("sr-section-title", isLight && "sr-section-title--light")}
      >
        {title}
      </TitleTag>

      {divider && (
        <span
          className={cn("sr-divider-gold", align === "center" && "mx-auto")}
          aria-hidden="true"
        />
      )}

      {description && (
        <p
          className={cn(
            "sr-section-description mt-1",
            isLight && "sr-section-description--light",
            align === "center" && "mx-auto"
          )}
        >
          {description}
        </p>
      )}
    </m.div>
  );
}
